import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, FileSearch, Loader2, PackageSearch } from "lucide-react";
import { Layout } from "@/components/layout";
import { FileDropzone } from "@/components/file-dropzone";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface CheckRow {
  rowNumber: number;
  part: string;
  quantity: number;
  uploadedCost: number;
  uploadedValue: number;
  lastInvoicedCost: number | null;
  lastInvoicedValue: number | null;
  lastInvoice: string | null;
  lastInvoiceDate: string | null;
  supplier: string | null;
  difference: number | null;
  absoluteDifference: number | null;
  pbookCost: number | null;
  pbookValue: number | null;
  pbookExactQuantityMatch: boolean | null;
  pbookAppliedQuantity: number | null;
  closestToPbook: "uploaded" | "last_invoiced" | "match" | null;
}

interface CheckResult {
  filename: string;
  month: string;
  monthEnd: string;
  rowCount: number;
  differenceCount: number;
  missingHistoricalCount: number;
  pbookWarning: string | null;
  results: CheckRow[];
}

const money = (value: number | null) =>
  value == null ? "—" : value.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const unitCost = (value: number | null) =>
  value == null ? "—" : value.toLocaleString("en-GB", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const displayDate = (value: string | null) =>
  value?.match(/^\d{4}-\d{2}-\d{2}$/) ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}` : value ?? "—";

export default function CostPriceChecksPage() {
  const { toast } = useToast();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CheckResult | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a spreadsheet first");
      const data = new FormData();
      data.append("file", file);
      data.append("month", month);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 90_000);
      let response: Response;
      try {
        response = await fetch(`${BASE}/api/cost-price-checks/run`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "XMLHttpRequest" },
          body: data,
          signal: controller.signal,
        });
      } catch (error: any) {
        if (error?.name === "AbortError") {
          throw new Error("The price check timed out after 90 seconds. Please try a smaller spreadsheet.");
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
      const raw = await response.text();
      let body: any = null;
      try {
        body = raw ? JSON.parse(raw) : null;
      } catch {
        if (!response.ok) {
          throw new Error(`Cost price service unavailable (HTTP ${response.status}). Please try again.`);
        }
        throw new Error("Cost price service returned an invalid response");
      }
      if (!response.ok) throw new Error(body?.error || `Cost price check failed (HTTP ${response.status})`);
      return body as CheckResult;
    },
    onSuccess: setResult,
    onError: (error: Error) => toast({ title: "Check failed", description: error.message, variant: "destructive" }),
  });

  return (
    <Layout fullWidth>
      <div className="w-full space-y-6">
        <header>
          <h1 className="text-3xl font-display font-bold text-duffells-grey flex items-center gap-3">
            <FileSearch className="w-8 h-8 text-duffells-orange" />
            Cost Price Checks
          </h1>
          <p className="text-duffells-grey/70 mt-1">
            Compare uploaded costs with the latest POGRN cost on or before a selected month end, then use Pbook to investigate differences.
          </p>
        </header>

        <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
          <div className="grid md:grid-cols-[240px_1fr] gap-5 items-start">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Month end</label>
              <input
                type="month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-duffells-orange/30"
              />
              <p className="text-xs text-slate-500 mt-2">The latest POGRN transaction on or before the final day of this month is used.</p>
            </div>
            <div>
              <FileDropzone
                accept=".xlsx,.xls,.csv"
                maxSize={25 * 1024 * 1024}
                label="Drag and drop Excel spreadsheets with Part, Quantity and Price/Cost columns"
                onUpload={async (files) => {
                  setFile(files[0] ?? null);
                  setResult(null);
                }}
              />
              {file && <p className="text-sm text-slate-600 mt-2">Selected: <span className="font-medium">{file.name}</span></p>}
            </div>
          </div>
          <Button onClick={() => run.mutate()} disabled={!file || !month || run.isPending}>
            {run.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking prices…</> : "Run cost price check"}
          </Button>
        </section>

        {result && (
          <>
            <section className="grid sm:grid-cols-3 gap-4">
              <SummaryCard label="Spreadsheet lines" value={result.rowCount} />
              <SummaryCard label="Prices with differences" value={result.differenceCount} warn={result.differenceCount > 0} />
              <SummaryCard label="No historical POGRN found" value={result.missingHistoricalCount} warn={result.missingHistoricalCount > 0} />
            </section>

            {result.pbookWarning && result.differenceCount > 0 && (
              <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div><strong>Pbook note.</strong> {result.pbookWarning}</div>
              </div>
            )}

            <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Results — largest differences first</h2>
                <p className="text-xs text-slate-500 mt-1">{result.filename} · month end {displayDate(result.monthEnd)}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1500px] text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Part</th>
                      <th className="px-4 py-3 text-right">Qty</th>
                      <th className="px-4 py-3 text-right">Uploaded cost</th>
                      <th className="px-4 py-3 text-right">Historic cost</th>
                      <th className="px-4 py-3 text-right">Uploaded value</th>
                      <th className="px-4 py-3 text-right">Historic POGRN value</th>
                      <th className="px-4 py-3 text-right">Difference</th>
                      <th className="px-4 py-3 text-left">History source</th>
                      <th className="px-4 py-3 text-right">Pbook unit</th>
                      <th className="px-4 py-3 text-left">Assessment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results.map((row) => {
                      const differs = (row.absoluteDifference ?? 0) >= 0.005;
                      return (
                        <tr key={`${row.rowNumber}-${row.part}`} className={`border-t border-slate-100 ${differs ? "bg-amber-50/40" : ""}`}>
                          <td className="px-4 py-3 font-mono font-medium">{row.part}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{row.quantity}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{unitCost(row.uploadedCost)}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{unitCost(row.lastInvoicedCost)}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{money(row.uploadedValue)}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{money(row.lastInvoicedValue)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-semibold ${differs ? "text-amber-800" : "text-emerald-700"}`}>
                            {row.difference == null ? "—" : money(row.difference)}
                          </td>
                          <td className="px-4 py-3">
                            {row.lastInvoicedCost == null
                              ? <span className="text-slate-400">Not found</span>
                              : <span className="text-slate-600">STHIST POGRN</span>}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {unitCost(row.pbookCost)}
                            {row.pbookExactQuantityMatch === false && row.pbookAppliedQuantity != null && (
                              <div className="text-xs text-amber-700">
                                Using {row.pbookAppliedQuantity.toLocaleString("en-GB")}-unit break
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {!differs ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" />Matches</span>
                              : row.closestToPbook === "uploaded" ? <span className="font-medium text-blue-700">Pbook supports uploaded cost</span>
                              : row.closestToPbook === "last_invoiced" ? <span className="font-medium text-violet-700">Pbook supports historic POGRN cost</span>
                              : <span className="text-slate-500">Review required</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}

function SummaryCard({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${warn ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
        <PackageSearch className="w-5 h-5" />
      </div>
      <div><div className="text-2xl font-bold text-slate-900">{value}</div><div className="text-sm text-slate-500">{label}</div></div>
    </div>
  );
}